import { useMemo, useState } from 'react'

import { format } from 'date-fns'
import { Minus, Plus, Search, ShoppingCart } from 'lucide-react'

import { useProductContext } from '@/components/ProductContext'
import {
    useConfirmedHotelDayReservations,
    useHairSalonConfirmedDayReservations,
    useReservations,
} from '@/components/ReservationContext'
import { GenericProductModal } from '@/components/generic-product-modal'
import { StoreTicketModal } from '@/components/store-ticket-modal'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/shared/auth'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/shared/ui/alert-dialog'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Input } from '@/shared/ui/input'
import { Separator } from '@/shared/ui/separator'
import { HairSalonReservation, HotelReservation, ShopProduct } from '@monorepo/functions/src/types/reservations'

const categoryIcons: { [key: string]: string } = {
    Juguetes: '🧸',
    Alimentos: '🥗',
    Accesorios: '🎀',
}

type CartItem = {
    product: {
        id: string
        name: string
        price: number
        category?: string
        vat: number
    }
    quantity: number
    isGeneric?: boolean
}

type SaleMode = 'withReservation' | 'walkIn'
type StoreTicketItem = {
    name: string
    quantity: number
    totalPrice: number
    vat: number
}

export function VisualPOS() {
    const { products, categories, addSale } = useProductContext()
    const { updateReservation } = useReservations()
    const { user } = useAuth()
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
    const [selectedReservation, setSelectedReservation] = useState<HotelReservation | HairSalonReservation | null>(null)
    const [saleMode, setSaleMode] = useState<SaleMode>('withReservation')
    const [ticketItems, setTicketItems] = useState<StoreTicketItem[]>([])
    const [ticketClientName, setTicketClientName] = useState('')
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false)
    const [cart, setCart] = useState<CartItem[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    const [isSearchFocused, setIsSearchFocused] = useState(false)
    const { toast } = useToast()

    // Get today's date
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])

    // Get active reservations for today
    const { reservations: hotelReservations, isLoading: isLoadingHotel } = useConfirmedHotelDayReservations(today)
    const { reservations: hairSalonReservations, isLoading: isLoadingHairSalon } =
        useHairSalonConfirmedDayReservations(today)

    console.log({ hairSalonReservations })

    // Create searchable items from both reservation types
    const allReservations = useMemo(() => {
        // Cast hair salon reservations to get the correct type
        const hairSalonReservationsTyped = hairSalonReservations as unknown as HairSalonReservation[]
        return [...hotelReservations, ...hairSalonReservationsTyped]
    }, [hotelReservations, hairSalonReservations])

    // Helper functions to determine reservation type and extract common properties
    const isHotelReservation = (
        reservation: HotelReservation | HairSalonReservation,
    ): reservation is HotelReservation => {
        return 'pets' in reservation
    }

    const getClientName = (reservation: HotelReservation | HairSalonReservation) => {
        return reservation.client.name
    }

    const getPetInfo = (reservation: HotelReservation | HairSalonReservation) => {
        if (isHotelReservation(reservation)) {
            return {
                name: reservation.pets[0]?.name || 'Mascota',
                breed: reservation.pets[0]?.breed || 'Sin especificar',
                roomNumber: reservation.pets[0]?.roomNumber,
            }
        } else {
            return {
                name: reservation.pet.name,
                breed: reservation.pet.breed,
                roomNumber: undefined,
            }
        }
    }

    const getReservationProducts = (reservation: HotelReservation | HairSalonReservation) => {
        return (reservation.shopProducts || []).map((product: ShopProduct) => ({
            id: product.id,
            name: product.name,
            quantity: product.quantity || 1,
            price: product.unitPrice || 0,
            vat: product.vat || 21, // Default VAT is 21% if not specified
        }))
    }

    // Obtener el empleado seleccionado del sessionStorage
    const selectedEmployee = JSON.parse(sessionStorage.getItem('selectedEmployee') || '{}')

    const filteredProducts = selectedCategory
        ? products.filter(product => product.category === selectedCategory)
        : products

    // Obtener productos previamente agregados a la reserva
    const existingProducts = useMemo(() => {
        if (!selectedReservation) return []
        return getReservationProducts(selectedReservation)
    }, [selectedReservation])

    // Combinar productos existentes con el carrito actual para el conteo
    const productCount = useMemo(() => {
        const counts: { [key: string]: number } = {}

        // Contar productos existentes
        existingProducts.forEach((product: any) => {
            if (product.id && product.quantity) {
                counts[product.id] = (counts[product.id] || 0) + product.quantity
            }
        })

        // Añadir productos del carrito actual
        cart.forEach(item => {
            if (!item.isGeneric && item.product.id) {
                counts[item.product.id] = (counts[item.product.id] || 0) + item.quantity
            }
        })

        return counts
    }, [existingProducts, cart])

    const addToCart = (product: any, isGeneric: boolean = false) => {
        if (saleMode === 'withReservation' && !selectedReservation) {
            toast({
                title: 'Error',
                description: 'Por favor, selecciona una reserva antes de añadir productos',
                variant: 'destructive',
            })
            return
        }

        // Generate a unique ID for generic products
        if (isGeneric) {
            product = {
                ...product,
                id: `generic_${Date.now()}_${Math.random()}`,
                vat: product.vat || 21
            }
        } else {
            // For non-generic products, get the VAT from the category
            const category = categories.find(c => c.name === product.category)
            product = { ...product, vat: category?.vat || 21 }
        }

        const existingItem = cart.find(item =>
            isGeneric ? item.isGeneric && item.product.name === product.name : item.product.id === product.id,
        )
        if (existingItem) {
            setCart(
                cart.map(item =>
                    (isGeneric ? item.isGeneric && item.product.name === product.name : item.product.id === product.id)
                        ? { ...item, quantity: item.quantity + 1 }
                        : item,
                ),
            )
        } else {
            setCart([...cart, { product, quantity: 1, isGeneric }])
        }
    }

    const removeFromCart = (index: number) => {
        setCart(cart.filter((_, i) => i !== index))
    }

    const updateQuantity = (index: number, newQuantity: number) => {
        if (newQuantity === 0) {
            removeFromCart(index)
        } else {
            setCart(cart.map((item, i) => (i === index ? { ...item, quantity: newQuantity } : item)))
        }
    }

    const getTotalPrice = () => {
        return cart.reduce((total, item) => total + item.product.price * item.quantity, 0)
    }

    const handleConfirmProducts = async () => {
        if (cart.length === 0) return
        if (saleMode === 'withReservation' && !selectedReservation) return

        const totalPrice = getTotalPrice()
        const clientName =
            saleMode === 'withReservation' && selectedReservation
                ? getClientName(selectedReservation)
                : 'Venta sin reserva'
        const reservationId = saleMode === 'withReservation' ? selectedReservation?.id : null
        const soldTicketItems = cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            totalPrice: item.product.price * item.quantity,
            vat: item.product.vat,
        }))

        const newSales = cart.map(item => ({
            productId: item.product.id,
            productName: item.product.name,
            price: item.product.price * item.quantity,
            clientName,
            reservationId,
            saleType: saleMode,
            userId: user?.uid || '',
            userName: user?.displayName || 'Usuario desconocido',
            employeeId: selectedEmployee.id || '',
            employeeName: selectedEmployee.name || 'Empleado desconocido',
        }))

        await Promise.all(newSales.map(sale => addSale(sale)))

        if (saleMode === 'withReservation' && selectedReservation) {
            // Convert cart items to ShopProduct format for the reservation
            const newShopProducts = cart.map(item => ({
                id: item.product.id,
                name: item.product.name,
                quantity: item.quantity,
                unitPrice: item.product.price,
                totalPrice: item.product.price * item.quantity,
                vat: item.product.vat,
            }))

            // Update the reservation with new shop products
            const currentProducts = getReservationProducts(selectedReservation)
            const updatedShopProducts = [
                // Keep existing shop products from the original reservation
                ...currentProducts.map(
                    p =>
                        ({
                            id: p.id,
                            name: p.name,
                            quantity: p.quantity,
                            unitPrice: p.price,
                            totalPrice: p.price * p.quantity,
                            vat: p.vat || 21, // Default VAT is 21% for existing products without VAT
                        }) as ShopProduct,
                ),
                ...newShopProducts,
            ]

            const currentTotalPrice =
                selectedReservation.totalPrice ||
                (isHotelReservation(selectedReservation)
                    ? selectedReservation.totalPrice
                    : selectedReservation.finalPrice || selectedReservation.precioEstimado) ||
                0

            const updatedReservation = {
                shopProducts: updatedShopProducts,
                totalPrice: currentTotalPrice + totalPrice,
            }

            await updateReservation(selectedReservation.id, updatedReservation)
        }

        setCart([])
        setSelectedReservation(null)
        setTicketItems(soldTicketItems)
        setTicketClientName(clientName)
        setIsTicketModalOpen(true)
        toast({
            title: 'Venta registrada',
            description:
                saleMode === 'withReservation'
                    ? `Se han agregado ${cart.length} productos a la reserva de ${clientName} por un total de ${totalPrice.toFixed(2)} €`
                    : `Se registró una venta sin reserva por ${totalPrice.toFixed(2)} €`,
        })
    }

    const filteredReservations = allReservations.filter(r => {
        const searchLower = searchQuery.toLowerCase()
        const clientName = getClientName(r)
        const petInfo = getPetInfo(r)
        return (
            clientName.toLowerCase().includes(searchLower) ||
            petInfo.name.toLowerCase().includes(searchLower) ||
            petInfo.breed.toLowerCase().includes(searchLower) ||
            (petInfo.roomNumber && petInfo.roomNumber.toLowerCase().includes(searchLower))
        )
    })

    const isLoading = isLoadingHotel || isLoadingHairSalon

    if (isLoading) {
        return (
            <div className='flex h-full items-center justify-center'>
                <div className='text-center'>
                    <div className='text-lg font-medium'>Cargando reservas...</div>
                    <div className='text-sm text-gray-500'>Obteniendo reservas activas para hoy</div>
                </div>
            </div>
        )
    }

    return (
        <>
            {isTicketModalOpen && (
                <StoreTicketModal
                    items={ticketItems}
                    clientName={ticketClientName}
                    onClose={() => setIsTicketModalOpen(false)}
                />
            )}
            <div className='flex h-full gap-6'>
                {/* Panel izquierdo: Búsqueda y productos */}
                <div className='flex-1'>
                {/* Card 1: Búsqueda y selección de reserva */}
                <Card>
                    <CardContent className='p-6'>
                        <h2 className='mb-4 text-xl font-semibold'>Tipo de venta y reserva</h2>
                        <div className='mb-4 flex gap-2'>
                            <Button
                                variant={saleMode === 'withReservation' ? 'default' : 'outline'}
                                onClick={() => setSaleMode('withReservation')}
                            >
                                Con reserva
                            </Button>
                            <Button
                                variant={saleMode === 'walkIn' ? 'default' : 'outline'}
                                onClick={() => {
                                    setSaleMode('walkIn')
                                    setSelectedReservation(null)
                                }}
                            >
                                Sin reserva
                            </Button>
                        </div>
                        {saleMode === 'walkIn' && (
                            <div className='mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700'>
                                Venta rápida activa. Esta venta se guardará en histórico y facturación de tienda sin
                                asociarse a una reserva.
                            </div>
                        )}
                        <div className='relative'>
                            <Search className='absolute left-2 top-1/2 -translate-y-1/2 transform text-gray-400' />
                            <Input
                                placeholder='Buscar por cliente, mascota o habitación...'
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className='w-full pl-10'
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={() => setTimeout(() => setIsSearchFocused(false), 150)}
                                disabled={saleMode === 'walkIn'}
                            />
                        </div>

                        {saleMode === 'withReservation' && (isSearchFocused || searchQuery) && (
                            <div className='mt-4 rounded-md border bg-white p-2 shadow-sm'>
                                {(() => {
                                    const reservationsToShow = searchQuery ? filteredReservations : allReservations

                                    if (reservationsToShow.length === 0) {
                                        return (
                                            <div className='p-4 text-center text-gray-500'>
                                                {searchQuery
                                                    ? 'No se encontraron reservas que coincidan con la búsqueda'
                                                    : 'No hay reservas activas para hoy'}
                                            </div>
                                        )
                                    }

                                    return reservationsToShow.map(reservation => {
                                        const clientName = getClientName(reservation)
                                        const petInfo = getPetInfo(reservation)
                                        const isHotel = isHotelReservation(reservation)

                                        return (
                                            <div
                                                key={reservation.id}
                                                className={`cursor-pointer rounded-md p-2 transition-colors hover:bg-gray-50 ${
                                                    selectedReservation?.id === reservation.id ? 'bg-blue-50' : ''
                                                }`}
                                                onClick={() => {
                                                    setSelectedReservation(reservation)
                                                    setSearchQuery('')
                                                    setIsSearchFocused(false)
                                                }}
                                            >
                                                <div className='flex flex-col items-start'>
                                                    <div className='flex items-center gap-2'>
                                                        <span className='font-medium'>{clientName}</span>
                                                        <Badge variant='outline' className='text-xs'>
                                                            {isHotel ? 'Hotel' : 'Peluquería'}
                                                        </Badge>
                                                        {petInfo.roomNumber && (
                                                            <Badge variant='secondary' className='text-xs'>
                                                                {petInfo.roomNumber}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <span className='text-sm text-gray-600'>
                                                        Mascota: {petInfo.name} • {petInfo.breed}
                                                    </span>
                                                    {isHotel && reservation.checkOutTime && (
                                                        <span className='text-xs text-gray-500'>
                                                            Checkout: {reservation.checkOutDate} a las{' '}
                                                            {reservation.checkOutTime}
                                                        </span>
                                                    )}
                                                    {!isHotel && (reservation.time || reservation.horaDefinitiva) && (
                                                        <span className='text-xs text-gray-500'>
                                                            Cita: {reservation.time || reservation.horaDefinitiva}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })
                                })()}
                            </div>
                        )}

                        {saleMode === 'withReservation' && !isSearchFocused && !searchQuery && allReservations.length === 0 && (
                            <div className='mt-4 rounded-lg bg-gray-50 p-4 text-center'>
                                <p className='text-gray-600'>No hay reservas activas para hoy</p>
                                <p className='text-sm text-gray-500'>Las reservas confirmadas aparecerán aquí</p>
                            </div>
                        )}

                        {saleMode === 'withReservation' && selectedReservation && (
                            <div className='mt-4 rounded-lg bg-gray-50 p-4'>
                                <h3 className='font-medium'>Reserva seleccionada:</h3>
                                <div className='mt-1 flex items-center gap-2'>
                                    <p className='text-sm text-gray-600'>
                                        Cliente: {getClientName(selectedReservation)}
                                    </p>
                                    <Badge variant='outline' className='text-xs'>
                                        {isHotelReservation(selectedReservation) ? 'Hotel' : 'Peluquería'}
                                    </Badge>
                                    {(() => {
                                        const petInfo = getPetInfo(selectedReservation)
                                        return (
                                            petInfo.roomNumber && (
                                                <Badge variant='secondary' className='text-xs'>
                                                    {petInfo.roomNumber}
                                                </Badge>
                                            )
                                        )
                                    })()}
                                </div>
                                <p className='text-sm text-gray-600'>
                                    Mascota:{' '}
                                    {(() => {
                                        const petInfo = getPetInfo(selectedReservation)
                                        return `${petInfo.name} (${petInfo.breed})`
                                    })()}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Card 2: Selección y filtrado de productos */}
                <Card
                    className={
                        saleMode === 'withReservation' && !selectedReservation
                            ? 'pointer-events-none mt-6 opacity-50'
                            : 'mt-6'
                    }
                >
                    <CardContent className='p-6'>
                        <div className='mb-6 flex items-center justify-between'>
                            <h2 className='text-xl font-semibold'>Productos</h2>
                            <GenericProductModal onAddToCart={product => addToCart(product, true)} />
                        </div>

                        <div className='mb-6 flex flex-wrap gap-2'>
                            <Button
                                variant={selectedCategory === null ? 'default' : 'outline'}
                                onClick={() => setSelectedCategory(null)}
                                className='whitespace-nowrap px-4 py-2 text-base'
                            >
                                Todos
                            </Button>
                            {categories.map(category => (
                                <Button
                                    key={category.id}
                                    variant={selectedCategory === category.name ? 'default' : 'outline'}
                                    onClick={() => setSelectedCategory(category.name)}
                                    className='whitespace-nowrap px-4 py-2 text-base'
                                >
                                    {categoryIcons[category.name] || '📦'} {category.name}
                                </Button>
                            ))}
                        </div>

                        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
                            {filteredProducts.map(product => {
                                const count = productCount[product.id] || 0
                                return (
                                    <Card
                                        key={product.id}
                                        className='relative cursor-pointer transition-shadow hover:shadow-md'
                                        onClick={() => addToCart(product)}
                                    >
                                        <CardContent className='flex h-full flex-col items-center justify-center p-4'>
                                            <h3 className='text-center font-bold'>{product.name}</h3>
                                            <p className='mt-2 text-lg font-semibold'>{product.price.toFixed(2)} €</p>
                                            {count > 0 && (
                                                <Badge
                                                    variant='secondary'
                                                    className='absolute right-2 top-2 bg-blue-100 font-bold text-blue-700'
                                                >
                                                    {count}
                                                </Badge>
                                            )}
                                        </CardContent>
                                    </Card>
                                )
                            })}
                        </div>
                    </CardContent>
                </Card>
                </div>

                {/* Panel derecho: Carrito y productos existentes */}
                <div className='w-[300px]'>
                    <Card className='h-full'>
                    <CardHeader className='border-b'>
                        <div className='flex items-center justify-between'>
                            <CardTitle className='flex items-center'>
                                <ShoppingCart className='mr-2' />
                                Resumen del pedido
                            </CardTitle>
                            {selectedReservation && (
                                <p className='text-sm text-gray-600'>{getClientName(selectedReservation)}</p>
                            )}
                        </div>
                    </CardHeader>

                    <CardContent className='space-y-4 p-4'>
                        {/* Productos existentes */}
                        {existingProducts.length > 0 && (
                            <div className='space-y-2'>
                                <h3 className='text-sm font-medium text-muted-foreground'>Productos existentes</h3>
                                {existingProducts.map((item: any, index: number) => (
                                    <div key={`existing-${index}`} className='rounded-lg bg-muted/50 p-3'>
                                        <div className='flex items-center justify-between'>
                                            <div>
                                                <span className='font-medium'>{item.name}</span>
                                                <p className='text-sm text-muted-foreground'>
                                                    {(item.price * item.quantity).toFixed(2)} €
                                                </p>
                                            </div>
                                            <span className='text-sm font-medium'>x{item.quantity}</span>
                                        </div>
                                    </div>
                                ))}
                                <Separator className='my-4' />
                            </div>
                        )}

                        {/* Carrito actual */}
                        {cart.map((item, index) => (
                            <div key={index} className='rounded-lg bg-gray-50 p-3'>
                                <div className='flex items-center justify-between'>
                                    <div className='flex-1'>
                                        <span className='font-medium'>
                                            {item.isGeneric
                                                ? `Producto Genérico: ${item.product.name}`
                                                : item.product.name}
                                        </span>
                                        <p className='text-sm text-gray-600'>
                                            {(item.product.price * item.quantity).toFixed(2)} € (IVA: {item.product.vat}%)
                                        </p>
                                    </div>
                                    <div className='flex items-center gap-2'>
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={() => updateQuantity(index, item.quantity - 1)}
                                        >
                                            <Minus className='h-4 w-4' />
                                        </Button>
                                        <span className='w-8 text-center'>{item.quantity}</span>
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={() => updateQuantity(index, item.quantity + 1)}
                                        >
                                            <Plus className='h-4 w-4' />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </CardContent>

                    {cart.length > 0 && (
                        <div className='space-y-4 border-t p-4'>
                            <div className='flex justify-between'>
                                <span className='text-lg font-bold'>Total:</span>
                                <span className='text-lg font-bold'>{getTotalPrice().toFixed(2)} €</span>
                            </div>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button className='w-full bg-green-600 hover:bg-green-700'>Confirmar Pedido</Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>¿Confirmar pedido?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            {saleMode === 'withReservation'
                                                ? 'Esta acción agregará los productos seleccionados a la reserva.'
                                                : 'Esta acción registrará una venta de tienda sin reserva.'}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                        <AlertDialogAction onClick={handleConfirmProducts}>Confirmar</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    )}
                    </Card>
                </div>
            </div>
        </>
    )
}
